import {
    Bell,
    Check,
    ChevronDown,
    Heart,
    HelpCircle,
    History,
    Languages,
    LogOut,
    MapPin,
    Menu,
    MessageCircle,
    Package,
    Search,
    Settings,
    ShoppingCart,
    UserRound,
    WalletCards,
    X,
} from "lucide-react";
import {
    FormEvent,
    useEffect,
    useRef,
    useState,
} from "react";
import {
    useNavigate,
} from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { logout } from "../../../Redux/custmoer_auth/slice";
import {
    getSuggestions,
    clearSuggestions,
} from "../../../Redux/Search_apis/slice";
import { SuggestionResponseDto } from "../../../Redux/Search_apis/types";
import {
    recordActivity,
    getRecentSearches,
} from "../../../Redux/ActivityApi/slice";
import { ActivityType } from "../../../Redux/ActivityApi/types";

import {
    DEFAULT_USER_NAVBAR_PROFILE,
    USER_NAVBAR_COUNTRIES,
    USER_NAVBAR_CURRENCIES,
    USER_NAVBAR_LANGUAGES,
    UserNavbarProfile,
} from "./UserNavbarData";

type UserDesktopNavbarProps = {
    companyName?: string;
    cartCount?: number;
    cartTotal?: number;
    notificationCount?: number;
    messageCount?: number;
    profile?: UserNavbarProfile;
    onMenuClick?: () => void;
    menuOpen?: boolean;
};

const formatCartPrice = (value: number) =>
    new Intl.NumberFormat("en-IN", {
        maximumFractionDigits: 0,
    }).format(Math.max(0, value));

const UserDesktopNavbar = ({
    companyName = "SWORLDS SOLUTIONS PVT LTD",
    cartCount = 0,
    cartTotal = 0,
    notificationCount = 0,
    messageCount = 0,
    profile = DEFAULT_USER_NAVBAR_PROFILE,
    onMenuClick,
    menuOpen = false,
}: UserDesktopNavbarProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const suggestions = useAppSelector(
        (state) => state.search.suggestions.data
    );
    const recentSearches = useAppSelector(
        (state) => state.activity.recentSearches
    );

    const localeRef = useRef<HTMLDivElement>(null);
    const profileRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLDivElement>(null);

    const [searchValue, setSearchValue] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);

    const [localeOpen, setLocaleOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [countryOpen, setCountryOpen] = useState(false);
    const [languageOpen, setLanguageOpen] = useState(false);
    const [currencyOpen, setCurrencyOpen] = useState(false);

    const [country, setCountry] = useState("IN");
    const [language, setLanguage] = useState("English");
    const [currency, setCurrency] = useState("INR");

    const selectedCountry =
        USER_NAVBAR_COUNTRIES.find((item) => item.code === country) ??
        USER_NAVBAR_COUNTRIES[0];

    const selectedLanguage =
        USER_NAVBAR_LANGUAGES.find((item) => item.name === language) ??
        USER_NAVBAR_LANGUAGES[0];

    const selectedCurrency =
        USER_NAVBAR_CURRENCIES.find((item) => item.code === currency) ??
        USER_NAVBAR_CURRENCIES[0];

    // Debounced search suggestions query
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchValue.trim().length > 1) {
                dispatch(getSuggestions(searchValue));
                setShowSuggestions(true);
            } else {
                dispatch(clearSuggestions());
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchValue, dispatch]);

    // Handle outside clicks
    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node;

            if (localeRef.current && !localeRef.current.contains(target)) {
                setLocaleOpen(false);
                setCountryOpen(false);
                setLanguageOpen(false);
                setCurrencyOpen(false);
            }

            if (profileRef.current && !profileRef.current.contains(target)) {
                setProfileOpen(false);
            }

            if (searchRef.current && !searchRef.current.contains(target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () =>
            document.removeEventListener("mousedown", handleOutsideClick);
    }, []);

    const closeLocaleMenus = () => {
        setCountryOpen(false);
        setLanguageOpen(false);
        setCurrencyOpen(false);
    };

    const executeSearch = async (term: string) => {
        const query = term.trim();
        if (!query) {
            return;
        }

        await dispatch(
            recordActivity({
                activities: {
                    eventType: ActivityType.SEARCH,
                    searchQuery: query,
                },
            })
        );

        setShowSuggestions(false);
        navigate(`/Categories/All?q=${encodeURIComponent(query)}`);
    };

    const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        executeSearch(searchValue);
    };

    const handleSearchFocus = () => {
        dispatch(getRecentSearches(5));
        setShowSuggestions(true);
    };

    const handleLogout = () => {
        setProfileOpen(false);
        dispatch(logout());
        navigate("/HomePage");
    };

    const userInitial =
        profile.initial ||
        profile.name.trim().charAt(0).toUpperCase() ||
        "S";

    return (
        <header className="user-module-desktop-nav">
            <div className="user-module-desktop-nav-inner">
                <div className="user-module-desktop-brand">
                    <button
                        type="button"
                        className="user-module-logo"
                        onClick={() => navigate("/HomePage")}
                        aria-label="Go to Salar home"
                    >
                        <span className="user-module-logo-aura" />
                        <img
                            src="/assets/images/logos/salarlogo.png"
                            alt="Salar"
                        />
                    </button>

                    <button
                        type="button"
                        className={`user-module-menu-button ${menuOpen ? "active" : ""}`}
                        onClick={() => onMenuClick?.()}
                        aria-label={menuOpen ? "Close dashboard menu" : "Open dashboard menu"}
                        aria-expanded={menuOpen}
                    >
                        <Menu size={21} strokeWidth={1.95} />
                    </button>

                    <div className="user-module-company-copy">
                        <strong>{companyName}</strong>
                        <small>User Dashboard</small>
                    </div>
                </div>

                <div
                    ref={searchRef}
                    style={{ position: "relative", width: "100%", maxWidth: 610 }}
                >
                    <form
                        className="user-module-desktop-search"
                        onSubmit={handleSearchSubmit}
                    >
                        <Search size={18} strokeWidth={1.9} />

                        <input
                            type="search"
                            value={searchValue}
                            onChange={(event) => setSearchValue(event.target.value)}
                            onFocus={handleSearchFocus}
                            placeholder="Search products, brands and categories..."
                            aria-label="Search user dashboard"
                            autoComplete="off"
                        />

                        {searchValue && (
                            <button
                                type="button"
                                className="user-module-search-clear"
                                onClick={() => {
                                    setSearchValue("");
                                    setShowSuggestions(false);
                                }}
                                aria-label="Clear search"
                            >
                                <X size={15} strokeWidth={2} />
                            </button>
                        )}

                        <button
                            type="submit"
                            className="user-module-search-submit"
                            aria-label="Search"
                        >
                            <Search size={17} strokeWidth={2.1} />
                        </button>
                    </form>

                    {showSuggestions && (
                        <div className="user-module-search-suggestions">
                            {searchValue.trim().length > 1
                                ? suggestions.map(
                                      (
                                          suggestion: SuggestionResponseDto,
                                          index: number
                                      ) => (
                                          <div
                                              key={index}
                                              className="user-module-search-suggestion-item"
                                              onMouseDown={(e) => {
                                                  e.preventDefault();
                                                  setSearchValue(suggestion.text);
                                                  executeSearch(suggestion.text);
                                              }}
                                          >
                                              <Search size={15} strokeWidth={1.8} />
                                              <div>
                                                  <strong>{suggestion.text}</strong>
                                                  {suggestion.metadata?.categoryHierarchy?.category && (
                                                      <small>
                                                          In {suggestion.metadata.categoryHierarchy.category}
                                                      </small>
                                                  )}
                                              </div>
                                          </div>
                                      )
                                  )
                                : recentSearches && recentSearches.length > 0 && (
                                      <>
                                          <div className="user-module-search-suggestion-header">
                                              Recent Searches
                                          </div>
                                          {recentSearches.map(
                                              (item: string, index: number) => (
                                                  <div
                                                      key={index}
                                                      className="user-module-search-suggestion-item"
                                                      onMouseDown={(e) => {
                                                          e.preventDefault();
                                                          setSearchValue(item);
                                                          executeSearch(item);
                                                      }}
                                                  >
                                                      <History size={15} strokeWidth={1.8} />
                                                      <div>
                                                          <strong>{item}</strong>
                                                      </div>
                                                  </div>
                                              )
                                          )}
                                      </>
                                  )}
                        </div>
                    )}
                </div>

                <div className="user-module-desktop-actions">
                    <div className="user-module-locale-wrapper" ref={localeRef}>
                        <button
                            type="button"
                            className={`user-module-locale-trigger ${localeOpen ? "active" : ""}`}
                            onClick={() => {
                                const next = !localeOpen;
                                setLocaleOpen(next);
                                if (!next) {
                                    closeLocaleMenus();
                                }
                                setProfileOpen(false);
                            }}
                            aria-expanded={localeOpen}
                        >
                            <span className="user-module-action-icon">
                                <Languages size={18} strokeWidth={1.8} />
                            </span>

                            <span className="user-module-action-copy">
                                <small>Preferences</small>
                                <strong>
                                    {selectedCountry.flag} {selectedCountry.code} ·{" "}
                                    {selectedLanguage.shortName} · {selectedCurrency.code}
                                </strong>
                            </span>

                            <ChevronDown
                                size={14}
                                strokeWidth={2}
                                className={localeOpen ? "rotate" : ""}
                            />
                        </button>

                        {localeOpen && (
                            <div className="user-module-locale-dropdown">
                                <div className="user-module-dropdown-head">
                                    <span>
                                        <Languages size={20} strokeWidth={1.9} />
                                    </span>
                                    <div>
                                        <h4>Country, Language & Currency</h4>
                                        <p>Personalize your Salar experience.</p>
                                    </div>
                                </div>

                                <div className="user-module-preference-group">
                                    <label>Country</label>
                                    <div className="user-module-preference-wrapper">
                                        <button
                                            type="button"
                                            className="user-module-preference-trigger"
                                            onClick={() => {
                                                setCountryOpen((prev) => !prev);
                                                setLanguageOpen(false);
                                                setCurrencyOpen(false);
                                            }}
                                        >
                                            <span>
                                                <b className="user-module-preference-flag">
                                                    {selectedCountry.flag}
                                                </b>
                                                <span>
                                                    <small>Shopping country</small>
                                                    <strong>{selectedCountry.name}</strong>
                                                </span>
                                            </span>
                                            <ChevronDown
                                                size={15}
                                                className={countryOpen ? "rotate" : ""}
                                            />
                                        </button>

                                        {countryOpen && (
                                            <div className="user-module-preference-options">
                                                {USER_NAVBAR_COUNTRIES.map((item) => (
                                                    <button
                                                        type="button"
                                                        key={item.code}
                                                        className={country === item.code ? "selected" : ""}
                                                        onClick={() => {
                                                            setCountry(item.code);
                                                            setCountryOpen(false);
                                                        }}
                                                    >
                                                        <span>
                                                            <b>{item.flag}</b>
                                                            {item.name}
                                                        </span>
                                                        {country === item.code && (
                                                            <Check size={14} strokeWidth={2.5} />
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="user-module-preference-group">
                                    <label>Language</label>
                                    <div className="user-module-preference-wrapper">
                                        <button
                                            type="button"
                                            className="user-module-preference-trigger"
                                            onClick={() => {
                                                setLanguageOpen((prev) => !prev);
                                                setCountryOpen(false);
                                                setCurrencyOpen(false);
                                            }}
                                        >
                                            <span>
                                                <b className="user-module-preference-flag">
                                                    {selectedLanguage.flag}
                                                </b>
                                                <span>
                                                    <small>Display language</small>
                                                    <strong>{selectedLanguage.name}</strong>
                                                </span>
                                            </span>
                                            <ChevronDown
                                                size={15}
                                                className={languageOpen ? "rotate" : ""}
                                            />
                                        </button>

                                        {languageOpen && (
                                            <div className="user-module-preference-options">
                                                {USER_NAVBAR_LANGUAGES.map((item) => (
                                                    <button
                                                        type="button"
                                                        key={item.name}
                                                        className={language === item.name ? "selected" : ""}
                                                        onClick={() => {
                                                            setLanguage(item.name);
                                                            setLanguageOpen(false);
                                                        }}
                                                    >
                                                        <span>
                                                            <b>{item.flag}</b>
                                                            {item.name}
                                                        </span>
                                                        {language === item.name && (
                                                            <Check size={14} strokeWidth={2.5} />
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="user-module-preference-group">
                                    <label>Currency</label>
                                    <div className="user-module-preference-wrapper">
                                        <button
                                            type="button"
                                            className="user-module-preference-trigger"
                                            onClick={() => {
                                                setCurrencyOpen((prev) => !prev);
                                                setCountryOpen(false);
                                                setLanguageOpen(false);
                                            }}
                                        >
                                            <span>
                                                <b>{selectedCurrency.symbol}</b>
                                                <span>
                                                    <small>Preferred currency</small>
                                                    <strong>
                                                        {selectedCurrency.code} · {selectedCurrency.name}
                                                    </strong>
                                                </span>
                                            </span>
                                            <ChevronDown
                                                size={15}
                                                className={currencyOpen ? "rotate" : ""}
                                            />
                                        </button>

                                        {currencyOpen && (
                                            <div className="user-module-preference-options">
                                                {USER_NAVBAR_CURRENCIES.map((item) => (
                                                    <button
                                                        type="button"
                                                        key={item.code}
                                                        className={currency === item.code ? "selected" : ""}
                                                        onClick={() => {
                                                            setCurrency(item.code);
                                                            setCurrencyOpen(false);
                                                        }}
                                                    >
                                                        <span>
                                                            <b>{item.symbol}</b>
                                                            {item.code} · {item.name}
                                                        </span>
                                                        {currency === item.code && (
                                                            <Check size={14} strokeWidth={2.5} />
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="user-module-preference-apply"
                                    onClick={() => {
                                        setLocaleOpen(false);
                                        closeLocaleMenus();
                                    }}
                                >
                                    Apply preferences
                                </button>
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        className="user-module-cart-trigger"
                        onClick={() => navigate("/Cart")}
                        aria-label="Open cart"
                    >
                        <span className="user-module-action-icon">
                            <ShoppingCart size={19} strokeWidth={1.9} />
                            {cartCount > 0 && (
                                <span className="user-module-count">
                                    {cartCount > 99 ? "99+" : cartCount}
                                </span>
                            )}
                        </span>

                        <span className="user-module-action-copy">
                            <small>Cart</small>
                            <strong>
                                {cartCount > 0
                                    ? `₹${formatCartPrice(cartTotal)} · ${cartCount} ${cartCount === 1 ? "item" : "items"}`
                                    : "Your cart"}
                            </strong>
                        </span>
                    </button>

                    <span className="user-module-action-divider" />

                    <button
                        type="button"
                        className="user-module-round-action"
                        onClick={() => navigate("/Offers")}
                        aria-label="Notifications"
                    >
                        <Bell size={19} strokeWidth={1.9} />
                        {notificationCount > 0 && (
                            <span className="user-module-mini-badge green">
                                {notificationCount > 99 ? "99+" : notificationCount}
                            </span>
                        )}
                    </button>

                    <button
                        type="button"
                        className="user-module-round-action"
                        onClick={() => navigate("/Tickets")}
                        aria-label="Messages and Support"
                    >
                        <MessageCircle size={19} strokeWidth={1.9} />
                        {messageCount > 0 && (
                            <span className="user-module-mini-badge orange">
                                {messageCount > 99 ? "99+" : messageCount}
                            </span>
                        )}
                    </button>

                    <div className="user-module-profile-wrapper" ref={profileRef}>
                        <button
                            type="button"
                            className={`user-module-profile-trigger ${profileOpen ? "active" : ""}`}
                            onClick={() => {
                                setProfileOpen((prev) => !prev);
                                setLocaleOpen(false);
                                closeLocaleMenus();
                            }}
                            aria-expanded={profileOpen}
                        >
                            <span className="user-module-avatar">
                                {profile.image ? (
                                    <img src={profile.image} alt={profile.name} />
                                ) : (
                                    <span>{userInitial}</span>
                                )}
                            </span>

                            <span className="user-module-profile-copy">
                                <strong>{profile.name}</strong>
                                <small>{profile.userId}</small>
                            </span>

                            <ChevronDown
                                size={14}
                                strokeWidth={2}
                                className={profileOpen ? "rotate" : ""}
                            />
                        </button>

                        {profileOpen && (
                            <div className="user-module-profile-dropdown">
                                <div className="user-module-profile-dropdown-head">
                                    <span className="user-module-avatar large">
                                        {profile.image ? (
                                            <img src={profile.image} alt={profile.name} />
                                        ) : (
                                            <span>{userInitial}</span>
                                        )}
                                    </span>

                                    <div>
                                        <strong>{profile.name}</strong>
                                        <small>{profile.email || profile.userId}</small>
                                    </div>
                                </div>

                                <div className="user-module-profile-menu">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate("/PersonalDetails");
                                        }}
                                    >
                                        <span>
                                            <UserRound size={17} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>My Profile</strong>
                                            <small>Personal details and KYC</small>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate("/Dashboard");
                                        }}
                                    >
                                        <span>
                                            <Settings size={17} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>Dashboard</strong>
                                            <small>Overview and activity</small>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate("/MyOrders");
                                        }}
                                    >
                                        <span>
                                            <Package size={17} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>My Orders</strong>
                                            <small>Track, return and manage purchases</small>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate("/SalarPay");
                                        }}
                                    >
                                        <span>
                                            <WalletCards size={17} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>My Wallet</strong>
                                            <small>Balance, payments and transfers</small>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate("/Wishlist");
                                        }}
                                    >
                                        <span>
                                            <Heart size={17} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>My Wishlist</strong>
                                            <small>View saved products</small>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate("/ManageAddress");
                                        }}
                                    >
                                        <span>
                                            <MapPin size={17} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>Manage Addresses</strong>
                                            <small>Shipping and billing locations</small>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate("/Tickets");
                                        }}
                                    >
                                        <span>
                                            <HelpCircle size={17} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>Help & Support</strong>
                                            <small>Customer tickets & chat support</small>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        className="logout"
                                        onClick={handleLogout}
                                    >
                                        <span>
                                            <LogOut size={17} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>Logout</strong>
                                            <small>Sign out of your account</small>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default UserDesktopNavbar;
